/**
 * Motor adaptativo del test de nivel.
 *
 * ⚠️ PORTADO VERBATIM del prototipo `docs/Plan test de nivel/test-nivel-completo-v2.jsx`.
 * Las fórmulas y constantes NO se tocan: el nivel resultante depende byte a byte
 * de esta lógica (corrección de adivinación 3PL con c=0.33, selección incremental
 * de ítems con jitter, actualización continua de θ con paso K decreciente y θ por
 * competencia con paso 0.5). `pickNext` usa `Math.random()` igual que el prototipo;
 * el test de paridad lo fija para comprobar equivalencia.
 */

import { BANK, GUESS, CEFR } from './levelTestBank';
import type { Skill } from './levelTestBank';

export const clampLvl = (n: number): number => Math.max(1, Math.min(6, n));

export const correctIdx = (item: { opts: [string, number][] }): number =>
  item.opts.findIndex((o) => o[1] === 1);

// 3PL: probabilidad de acierto con suelo de adivinación c
export const P3 = (d: number, th: number, c = GUESS, s = 1.0): number =>
  c + (1 - c) / (1 + Math.pow(10, (d - th) / s));

export function levelRange(theta: number): string {
  const lo = clampLvl(Math.round(theta - 0.4));
  const hi = clampLvl(Math.round(theta + 0.4));
  return lo === hi ? CEFR[lo - 1]! : `${CEFR[lo - 1]}–${CEFR[hi - 1]}`;
}

/**
 * Elige el índice (en BANK) del siguiente ítem no visto: el de dificultad `d` más
 * cercana a θ, con azar entre los cercanos y rotando competencias (Listening ~50 %).
 */
export function pickNext(th: number, used: number[], n: number): number | null {
  const pool = BANK.map((_, i) => i).filter((i) => !used.includes(i));
  if (pool.length === 0) return null;
  const want: Skill = n % 2 === 0 ? 'L' : n % 4 === 1 ? 'G' : 'V';
  const byDist = [...pool].sort((x, y) => Math.abs(BANK[x]!.d - th) - Math.abs(BANK[y]!.d - th));
  const minD = Math.abs(BANK[byDist[0]!]!.d - th);
  const near = byDist.filter((i) => Math.abs(BANK[i]!.d - th) <= minD + 0.6);
  const pref = near.filter((i) => BANK[i]!.skill === want);
  const choose = pref.length ? pref : near;
  return choose[Math.floor(Math.random() * choose.length)] ?? null;
}

/** Actualiza θ global tras una respuesta (paso K decreciente). */
export function updateTheta(theta: number, d: number, n: number, ok: boolean): number {
  const K = Math.max(0.25, 0.6 - 0.03 * n);
  const P = P3(d, theta);
  return clampLvl(ok ? theta + K * (1 - P) : theta - K * P);
}

/** Actualiza la θ de una competencia tras una respuesta (paso fijo 0.5). */
export function updateSkillTheta(skillTheta: number, d: number, ok: boolean): number {
  const Ps = P3(d, skillTheta);
  return clampLvl(ok ? skillTheta + 0.5 * (1 - Ps) : skillTheta - 0.5 * Ps);
}

/** Confianza derivada de la desviación de θ en las últimas 5 respuestas. */
export function computeConfidence(hist: number[]): 'Alta' | 'Media' | 'Baja' {
  if (hist.length < 3) return 'Media';
  const last = hist.slice(-5);
  const m = last.reduce((s, b) => s + b, 0) / last.length;
  const sd = Math.sqrt(last.reduce((s, b) => s + (b - m) ** 2, 0) / last.length);
  return sd < 0.35 ? 'Alta' : sd < 0.7 ? 'Media' : 'Baja';
}
